import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';

/** Mirrors ProductHuntCreditStatus in
 *  cleanse_api/models/billing/producthunt_credit.py. */
export type ProductHuntCreditStatus = 'pending' | 'approved' | 'rejected';

/** Mirrors ProductHuntRewardKind. */
export type ProductHuntRewardKind = 'upvote' | 'comment';

/** Mirrors ProductHuntCreditResponse in
 *  cleanse_api/api/billing/producthunt_credits.py. */
export interface ProductHuntCreditRequest {
  id: string;
  organization_id: string;
  user_id: string;
  reward_kind: ProductHuntRewardKind;
  evidence_url: string;
  status: ProductHuntCreditStatus;
  credits_awarded: number;
  reviewed_by: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  created_at: string | null;
}

/** Mirrors ProductHuntCreditStatusResponse — this org's latest request per
 *  tier (either may be null if never submitted). */
export interface ProductHuntCreditStatusResponse {
  upvote: ProductHuntCreditRequest | null;
  comment: ProductHuntCreditRequest | null;
}

/** Mirrors ProductHuntCreditApproveResponse. */
export interface ProductHuntCreditApproveResponse {
  request: ProductHuntCreditRequest;
  credits_granted: number;
  new_balance: number;
}

export const productHuntCreditsApi = createApi({
  reducerPath: 'productHuntCreditsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['ProductHuntCredit'],
  endpoints: (builder) => ({
    // The caller's org's latest request per tier.
    getMyProductHuntCredits: builder.query<
      ProductHuntCreditStatusResponse,
      void
    >({
      query: () => '/producthunt-credits/me',
      providesTags: [{ type: 'ProductHuntCredit', id: 'MINE' }],
    }),

    // Submit support evidence for a tier. 409 if the org already has a
    // pending/approved request of that kind; 400 if the URL isn't a valid
    // Product Hunt link.
    submitProductHuntCredit: builder.mutation<
      ProductHuntCreditRequest,
      { reward_kind: ProductHuntRewardKind; evidence_url: string }
    >({
      query: (body) => ({
        url: '/producthunt-credits',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'ProductHuntCredit', id: 'MINE' }],
    }),

    // Staff-only (server gates on @cleanlist.ai → 403 otherwise).
    adminListPendingProductHuntCredits: builder.query<
      ProductHuntCreditRequest[],
      { limit?: number; offset?: number } | void
    >({
      query: (args) => ({
        url: '/producthunt-credits/pending',
        params: {
          limit: args?.limit ?? 50,
          offset: args?.offset ?? 0,
        },
      }),
      providesTags: [{ type: 'ProductHuntCredit', id: 'PENDING' }],
    }),

    adminApproveProductHuntCredit: builder.mutation<
      ProductHuntCreditApproveResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/producthunt-credits/${id}/approve`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'ProductHuntCredit', id: 'PENDING' }],
    }),

    adminRejectProductHuntCredit: builder.mutation<
      ProductHuntCreditRequest,
      { id: string; reason?: string }
    >({
      query: ({ id, reason }) => ({
        url: `/producthunt-credits/${id}/reject`,
        method: 'POST',
        body: { reason: reason || null },
      }),
      invalidatesTags: [{ type: 'ProductHuntCredit', id: 'PENDING' }],
    }),
  }),
});

export const {
  useGetMyProductHuntCreditsQuery,
  useSubmitProductHuntCreditMutation,
  useAdminListPendingProductHuntCreditsQuery,
  useAdminApproveProductHuntCreditMutation,
  useAdminRejectProductHuntCreditMutation,
} = productHuntCreditsApi;
